import apiClient from './api.ts';
import {ParentInputSchema, RegisterParentSchema, ParentSearchResultSchema} from "@/types/parent_schema.ts";

export default {
  async registerParent(payload: RegisterParentSchema) {
    return await apiClient.post(`auth/register/parent`, payload);
  },
  async getParentAccountStatus(username: string): Promise<{ exists: boolean }> {
    const response = await apiClient.get(`auth/register/parent/account-status`, {
      params: { username },
    });
    return response.data.data;
  },
  async importParents(payload: RegisterParentSchema[]) {
    return await apiClient.post(`auth/register/parents`, payload);
  },
  async getParentBySchoolId(isActive: boolean, page: number = 1, limit: number = 20, sort: string = 'id', order: 'asc' | 'desc' = 'asc', search: string = '', searchField: string = '') {
    const params = new URLSearchParams({
      is_active: isActive.toString(),
      page: page.toString(),
      limit: limit.toString(),
      sort,
      order
    });
    if (search && search.length >= 2) {
      params.set('search', search);
      if (searchField) params.set('search_field', searchField);
    }
    return await apiClient.get(`parents?${params}`);
  },
  async getParentById(id: number) {
    return await apiClient.get(`parents/${id}`);
  },
  async updateParent(parentId: number, parent: ParentInputSchema) {
    return await apiClient.put(`parents/${parentId}`, parent);
  },
  async deleteParent(parentId: number) {
    return await apiClient.delete(`parents/${parentId}`);
  },
  async archiveParent(parentId: number) {
    return await apiClient.put(`parents/${parentId}/archive`);
  },
  async unarchiveParent(parentId: number) {
    return await apiClient.put(`parents/${parentId}/unarchive`);
  },
  async updatePhoneNumberParent(parentId: number, userId: number, oldPhoneNumber: string, phoneNumber: string) {
    const params = new URLSearchParams({
      oldPhoneNumber,
      phoneNumber
    });
    return await apiClient.put(`parents/${parentId}/user/${userId}/phone-number?${params}`);
  },
  async updateStudentParent(parentId: number, oldStudentId: number, newStudentId: number) {
    return await apiClient.put(`parents/${parentId}/old-student/${oldStudentId}/new-student/${newStudentId}`);
  },
  async addOrRemoveStudentFromParent(parentId: number, type: string, studentIds: number[]) {
    return await apiClient.post(`parents/${parentId}/student/update`, { student_ids: studentIds, type });
  },
  async uploadProfile(parentId: number, data: FormData) {
    return await apiClient.post(`parents/${parentId}/profile/upload`, data, { headers: {
        "Content-Type": "multipart/form-data"
      } });
  },
  async getStudentByParentId(parentId: number) {
    return await apiClient.get(`parents/${parentId}/students`);
  },
  async downloadExcelTemplate(count: number = 10, includeUsername: boolean = false, includePassword: boolean = false, lang: string = 'en') {
    const params = new URLSearchParams({
      count: count.toString(),
      lang: lang
    });

    if (includeUsername) {
      params.append('include_username', 'true');
    }

    if (includePassword) {
      params.append('include_password', 'true');
    }

    return await apiClient.get(`parents/template/excel?${params}`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
  },

  // Parent search functionality for enhanced student registration
  async searchParentsByName(name: string): Promise<ParentSearchResultSchema[]> {
    const params = new URLSearchParams({ name });
    const response = await apiClient.get(`enhanced-student-parent/search-parents?${params}`);
    return response.data?.data?.parents || [];
  },

  async searchParentsByPhone(phone: string): Promise<ParentSearchResultSchema[]> {
    const params = new URLSearchParams({ phone });
    const response = await apiClient.get(`enhanced-student-parent/search-parents?${params}`);
    return response.data?.data?.parents || [];
  },

  async searchParents(query: string, searchType: 'name' | 'phone' = 'name'): Promise<ParentSearchResultSchema[]> {
    if (searchType === 'phone') {
      return this.searchParentsByPhone(query);
    } else {
      return this.searchParentsByName(query);
    }
  },

  // Get relationship types for parent-student linking
  async getRelationshipTypes() {
    const response = await apiClient.get('enhanced-student-parent/relationship-types');
    return response.data?.data || { relationship_types: [], categories: [], by_category: {} };
  },

  // Student search functionality for parent registration
  async searchStudentsByName(name: string) {
    const params = new URLSearchParams({ name });
    const response = await apiClient.get(`enhanced-student-parent/search-students?${params}`);
    return response.data?.data?.students || [];
  },

  async searchStudentsByEmail(email: string) {
    const params = new URLSearchParams({ email });
    const response = await apiClient.get(`enhanced-student-parent/search-students?${params}`);
    return response.data?.data?.students || [];
  },

  async searchStudentsByStudentNumber(student_number: string) {
    const params = new URLSearchParams({ student_id: student_number });
    const response = await apiClient.get(`enhanced-student-parent/search-students?${params}`);
    return response.data?.data?.students || [];
  },

  async searchStudentsByPhone(phone: string) {
    const params = new URLSearchParams({ phone });
    const response = await apiClient.get(`enhanced-student-parent/search-students?${params}`);
    return response.data?.data?.students || [];
  },

  async searchStudents(query: string, searchType: 'name' | 'email' | 'student_number' | 'phone' = 'name') {
    if (searchType === 'email') {
      return this.searchStudentsByEmail(query);
    } else if (searchType === 'student_number') {
      return this.searchStudentsByStudentNumber(query);
    } else if (searchType === 'phone') {
      return this.searchStudentsByPhone(query);
    } else {
      return this.searchStudentsByName(query);
    }
  },

  // Register parent with existing students
  async registerParentWithStudents(payload: {
    parent_info: {
      first_name: string;
      last_name: string;
      email?: string;
      phone_number: string;
      gender: string;
      date_of_birth?: string;
      job?: string;
      address?: {
        address: string;
        city: string;
        state: string;
        zip_code?: string;
        country: string;
      };
      create_user_account: boolean;
      username?: string;
      password?: string;
    };
    student_relationships: Array<{
      existing_student_id: number;
      relationship_type: string;
      priority_level: number;
      parent_permissions: {
        is_primary?: boolean;
        is_emergency_contact?: boolean;
        can_pickup?: boolean;
        can_view_grades?: boolean;
        can_view_attendance?: boolean;
        receive_notifications?: boolean;
      };
      notes?: string;
      status?: string;
    }>;
    branch_ids?: string[];
  }) {
    return await apiClient.post(`enhanced-student-parent/register-parent-with-students`, payload);
  },

  async resetPassword(userId: number, newPassword: string) {
    return await apiClient.post(`users/${userId}/reset-password`, { new_password: newPassword });
  },
};
